import { Money } from './cardDetailHeroTypes'

/**
 * Display helpers shared by the two hero variants. The full hero and the pinned
 * compact bar show the same cardholder and the same available/limit figures, so
 * these must not be forked between them — the same reasoning that lifted the
 * status pill into `StatusPill`.
 */

/**
 * The contract carries `cardholder.displayName` and no initials field, so the
 * avatar fallback is derived. Deliberately dumb — first letter of the first two
 * whitespace-separated tokens — rather than trying to be clever about particles,
 * suffixes, or multi-word surnames.
 */
export const initialsFrom = (displayName: string): string =>
  displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

/**
 * The number of minor-unit decimal places for a currency (2 for USD, 0 for JPY,
 * 3 for BHD). This is a per-currency constant, but reading it means building an
 * `Intl.NumberFormat` — costly to reconstruct every render for each amount — so
 * results are memoized by currency code. Falls back to 2 for any code Intl can't
 * resolve.
 */
const fractionDigitsCache = new Map<string, number>()
export const currencyFractionDigits = (currency: string): number => {
  const cached = fractionDigitsCache.get(currency)
  if (cached !== undefined) return cached
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    digits = 2
  }
  fractionDigitsCache.set(currency, digits)
  return digits
}

/**
 * Minor units → major units at the display edge. The scale is currency-aware:
 * most currencies are 2-decimal (÷100), but zero-decimal (JPY) and three-decimal
 * (BHD) currencies are not — a hardcoded ÷100 would misprice them. (Shared by
 * both hero variants, so the compact bar gets the same currency handling as the
 * full summary.)
 */
export const toDisplayAmount = (money: Money): number =>
  money.amountMinor / 10 ** currencyFractionDigits(money.currency)

/**
 * The hero's credit meter fill, in whole minor units so the ratio is exact
 * regardless of currency decimal scale. Fill is the USED portion
 * (limit − available) — a mostly-full bar means little credit is left, matching
 * the design's own worked example ("$3,250 remaining" against a much larger
 * limit renders as a mostly-filled bar, not a mostly-empty one).
 *
 * A non-positive limit has no meaningful ratio (and would divide by zero), so
 * it renders empty rather than NaN.
 */
export const creditMeterPercent = (limit: Money, available: Money): number => {
  if (limit.amountMinor <= 0) return 0
  const usedPercent =
    ((limit.amountMinor - available.amountMinor) / limit.amountMinor) * 100
  return Number.isFinite(usedPercent)
    ? Math.min(100, Math.max(0, usedPercent))
    : 0
}
